/* Copyright (c) 2022 Xperi. All Rights Reserved. */

import SparkMD5 from 'spark-md5';

import {
    APP_ENVIRONMENT,
    BACKDOOR_PASSCODE,
    DEBUG_SCREEN_PASSCODE,
    SAGE_LOGGING_VERSION,
    TIZEN_PLATFORM,
} from '../../config';
import { UnauthenticatedError, UnauthorisedError } from '../error';
import { Log } from '../logging/Log';
import { SharedPreferenceMode, SharedPreferences } from '../storage';
import { Storage } from '../storage/Storage';
import { MessageService } from './MessageService';
import { OverrideFeatures } from './OverrideFeatures';
import { Settings } from './Settings';
import { TivoSettings } from './TivoSettings';
import { TvProperties } from './TvProperties';

export class DeviceModel {
    private static oTVProperties: TvProperties = new TvProperties();

    private static oTivoSettings: TivoSettings = new TivoSettings();

    private static oFlavour: string = '';

    private static oAppEnvironment: string = '';

    private static oOverrideFeatures: OverrideFeatures = new OverrideFeatures();

    private static mAccessToken: { accessToken: string; tokenExpirationTime: Date };

    private static mSamlToken: { samlToken: string; tokenExpirationTime: Date };

    private static mCaDeviceId: string = '';

    private static mDeviceUuid: string = '';

    private static mSessionId: string = '';

    private static mWanIpAddress: string = '';

    private static TAG = 'DeviceModel';

    private static WEBOS_PLATFORM = 'webOS';

    public static async initialize() {
        try {
            const [, , , appEnv, flavour] = await Promise.all([
                DeviceModel.oTVProperties.initialize(),
                DeviceModel.oTivoSettings.initialize(),
                DeviceModel.oOverrideFeatures.initialize(),
                new MessageService(MessageService.GET_APP_ENVIRONMENT).getResponse(),
                new MessageService(MessageService.GET_FLAVOUR).getResponse(),
            ]);
            await DeviceModel.oTivoSettings.overrideTivoSettings(DeviceModel.oOverrideFeatures);
            DeviceModel.oAppEnvironment = appEnv === true ? 'dev' : <string>appEnv;
            DeviceModel.oFlavour = <string>flavour;
            DeviceModel.initDeviceUuid();
            DeviceModel.initSessionId();
            DeviceModel.initCaDeviceId();
        } catch (error) {
            Log.e(DeviceModel.TAG, <string>error);
            throw new Error('Not a real device.');
        }
    }

    // The `appVersionName` will get updated at the time of build process. During the development,
    // the `appVersionName` will be `development-${packageJson.version}` by default.
    // NOTE: Please do not remove __PREBUILD_APP_VER__ tag.
    private static APP_VERSION_NAME: string = '4.1.1-rc.4.1-mr';

    // The `APP_HOME_PAGE` will get updated at the time of build process. During the development,
    // the `APP_HOME_PAGE` will be `/` by default.
    // NOTE: Please do not remove __PREBUILD_APP_HOME_PAGE__ tag.
    private static APP_HOME_PAGE: string = '/v4.1.1-rc.4.1-mr';

    /**
     * Generate a random cryptographic seed.
     * @param length The length of the seed to generate.
     * @returns A random cryptographic seed.
     */
    private static getRandomCryptoSeed(length: number = 32): string {
        const base = '0123456789abcdef';
        let seed = '';
        let size = seed.length;
        while (size < length) {
            const code = base.charAt(Math.floor(Math.random() * 100) % 16);
            seed += code;
            size = seed.length;
        }
        return seed;
    }

    public static generateUniqueId(seed: string): string {
        let id = SparkMD5.hash(seed);
        // format the user id in below format:
        // 27767c65-f950-4509-86dc-b356848472c9
        const match = id.match(/^(\w{8})(\w{4})(\w{4})(\w{4})(\w+)$/);
        if (match) {
            id = `${match[1]}-${match[2]}-${match[3]}-${match[4]}-${match[5]}`;
        }
        return id;
    }

    /**
     * Initialize the CA Device ID.
     * If the app is running inside an iframe, generate a CA Device ID based on the
     * HSN and MSO name. Otherwise, retrieve it from storage or generate a new one.
     */
    private static initCaDeviceId() {
        const browserCaDeviceId = DeviceModel.generateUniqueId(
            `caDeviceId-${DeviceModel.getRandomCryptoSeed()}`,
        );
        DeviceModel.mCaDeviceId =
            Storage.getInstance().getItem(Storage.Keys.CA_DEVICE_ID) || browserCaDeviceId;
        if (DeviceModel.inFrame()) {
            const seed = `${DeviceModel.getTVProperties().getHsn()}-${DeviceModel.getTiVoSettings().getMsoName()}`;
            DeviceModel.mCaDeviceId = DeviceModel.generateUniqueId(seed);
        } else {
            Storage.getInstance().setItem(Storage.Keys.CA_DEVICE_ID, DeviceModel.mCaDeviceId);
        }
    }

    /**
     * Initialize the Device UUID.
     * The Device UUID is retrieved from storage if it exists, otherwise a new one is generated
     * based on a random cryptographic seed and stored.
     */
    private static initDeviceUuid() {
        const storedDeviceUuid = Storage.getInstance().getItem(Storage.Keys.DEVICE_UUID);
        if (storedDeviceUuid) {
            DeviceModel.mDeviceUuid = storedDeviceUuid;
        } else {
            DeviceModel.mDeviceUuid = DeviceModel.generateUniqueId(
                `uuid-${DeviceModel.getRandomCryptoSeed()}`,
            );
            Storage.getInstance().setItem(Storage.Keys.DEVICE_UUID, DeviceModel.mDeviceUuid);
        }
    }

    /**
     * Initialize the Session ID.
     * The Session ID is generated based on a random cryptographic seed.
     */
    private static initSessionId() {
        if (!DeviceModel.mSessionId) {
            DeviceModel.mSessionId = DeviceModel.generateUniqueId(
                `sessionId-${DeviceModel.getRandomCryptoSeed()}`,
            );
        }
    }

    public static isValidDevice() {
        const browserAllowed = DeviceModel.oTivoSettings.isBrowserAllowed();
        if (browserAllowed) return true;
        return (
            DeviceModel.inFrame() &&
            (DeviceModel.isWebOsPlatform() || DeviceModel.isTizenPlatform())
        );
    }

    public static inFrame() {
        try {
            return window.self !== window.top || (window as any).__IS_VEGA_OS_PLATFORM__;
        } catch (e) {
            return true;
        }
    }

    public static isTizenPlatform() {
        return DeviceModel.getTVProperties().getPlatformName() === TIZEN_PLATFORM;
    }

    public static closeApplication() {
        if (DeviceModel.inFrame()) {
            new MessageService(MessageService.CLOSE_APPLICATION).getResponse().catch((error) => {
                Log.e(this.TAG, error);
            });
        } else {
            window.close();
        }
    }

    public static getTVProperties(): TvProperties {
        return DeviceModel.oTVProperties;
    }

    public static getTiVoSettings(): TivoSettings {
        return DeviceModel.oTivoSettings;
    }

    public static getFlavour() {
        return DeviceModel.oFlavour;
    }

    public static getAppEnvironment() {
        return DeviceModel.oAppEnvironment || 'production';
    }

    public static getAppName() {
        return DeviceModel.oTivoSettings.getAppName() || 'CableCo5';
    }

    public static getAppVersionName() {
        return DeviceModel.APP_VERSION_NAME;
    }

    public static getAppHomePage() {
        return DeviceModel.APP_HOME_PAGE;
    }

    public static getCaDeviceId(): string {
        return DeviceModel.mCaDeviceId;
    }

    public static getDeviceUuid(): string {
        return DeviceModel.mDeviceUuid;
    }

    public static getAppSettings(): Settings {
        return new Settings();
    }

    public static getFriendlyName(): string {
        return `${DeviceModel.oTVProperties.getPlatformName()} ${DeviceModel.oTVProperties.getDeviceModelCode()}`;
    }

    public static is1080P() {
        return (window as any).screen.height === 1080;
    }

    public static isProductionEnvironment() {
        return DeviceModel.getAppEnvironment() === APP_ENVIRONMENT.PRODUCTION;
    }

    public static isWebOsPlatform() {
        return DeviceModel.getTVProperties().getPlatformName() === DeviceModel.WEBOS_PLATFORM;
    }

    public static launchSystemSetting() {
        new MessageService(MessageService.LAUNCH_SYSTEM_SETTING).getResponse().catch((error) => {
            Log.e(this.TAG, error);
        });
    }

    public static setAccessTokenWithExpiry(accessToken: string, tokenExpirationTime: Date) {
        DeviceModel.mAccessToken = { accessToken, tokenExpirationTime };
    }

    public static getAccessToken() {
        return DeviceModel.mAccessToken?.accessToken;
    }

    public static getAccessTokenWithExpiry() {
        const doesAccessTokenExist =
            DeviceModel.mAccessToken && DeviceModel.mAccessToken?.accessToken;
        const isAccessTokenExpired =
            !DeviceModel.mAccessToken?.tokenExpirationTime ||
            DeviceModel.mAccessToken?.tokenExpirationTime.getTime() < new Date().getTime();
        if (!doesAccessTokenExist) throw new UnauthenticatedError();
        if (isAccessTokenExpired) throw new UnauthorisedError();
        return DeviceModel.mAccessToken;
    }

    public static setSamlTokenWithExpiry(samlToken: string, tokenExpirationTime: Date) {
        DeviceModel.mSamlToken = { samlToken, tokenExpirationTime };
    }

    public static getSamlToken() {
        return DeviceModel.mSamlToken?.samlToken;
    }

    public static getSamlTokenWithExpiry() {
        let samlToken = DeviceModel.mSamlToken;
        if (!samlToken || !samlToken.samlToken)
            samlToken = Storage.getInstance().getItem(Storage.Keys.SAML_TOKEN_WITH_EXPIRY);
        const doesSamlTokenExist = samlToken && samlToken.samlToken;
        const isSamlTokenExpired =
            !samlToken?.tokenExpirationTime ||
            new Date().getTime() > samlToken?.tokenExpirationTime.getTime();
        if (!doesSamlTokenExist || isSamlTokenExpired) {
            throw new Error('Saml Token expired or doesnot exist');
        }
        return samlToken;
    }

    public static getGlobalParams() {
        return <JSON>(<unknown>{
            appVersionName: DeviceModel.getAppVersionName(),
            caDeviceId: DeviceModel.getCaDeviceId(),
            connection: DeviceModel.getTVProperties().getActiveConnectionType(),
            device: DeviceModel.getTVProperties().getDeviceName(),
            deviceModel: DeviceModel.getTVProperties().getDeviceModel(),
            deviceModelCode: DeviceModel.getTVProperties().getDeviceModelCode(),
            deviceTsn: DeviceModel.getTiVoSettings().getDeviceTsn(),
            deviceType: DeviceModel.getTiVoSettings().getDeviceType(),
            localIP: DeviceModel.getTVProperties().getIpAddress(),
            loggingVersion: SAGE_LOGGING_VERSION,
            managedIP: DeviceModel.mWanIpAddress,
            osVersion: DeviceModel.getTVProperties().getPlatformVersion(),
            platformName: DeviceModel.getTVProperties().getPlatformName(),
            sessionId: DeviceModel.mSessionId,
            shellAppVersion: DeviceModel.getTiVoSettings().getShellVersion(),
            userIdentifier: DeviceModel.getTiVoSettings().getPartnerCustomerId(),
            uuid: DeviceModel.getDeviceUuid(),
        });
    }

    public static getSlsBaseUrl() {
        const slsBaseUrl = DeviceModel.oTivoSettings.getSlsBaseUrl();
        return this.oOverrideFeatures.getOverriddenSlsBaseUrl() || slsBaseUrl;
    }

    public static setWanIpAddress(wanIp: string) {
        DeviceModel.mWanIpAddress = wanIp;
    }

    public static getWanIpAddress(): string {
        return DeviceModel.mWanIpAddress;
    }

    public static isValidPasscode(passcode: string) {
        return passcode === BACKDOOR_PASSCODE;
    }

    public static isValidPasscodeForDebugScreen(passcode: string) {
        return passcode === DEBUG_SCREEN_PASSCODE;
    }

    public static enableBackdoor(passcode: string) {
        if (DeviceModel.isValidPasscode(passcode)) {
            const ENABLE_BACKDOOR: string = 'KaveriApp.BackdoorEntry.enableBackdoor';
            new MessageService(ENABLE_BACKDOOR).getResponse().catch((error) => {
                Log.e(this.TAG, 'Failed to enable backdoor entry', error);
            });
        }
    }

    public static restartApplication() {
        new MessageService(MessageService.RESTART_APPLICATION).getResponse().catch((error) => {
            Log.e(this.TAG, 'Failed to restartApplication', error);
        });
    }

    /**
     * The method will return the instance of `SharedPreferences`.
     * @param name (string) - The name of the shared perference.
     * @param mode (SharedPreferenceMode) - The mode of shared perference. Default is
     * `SharedPreferenceMode.SESSION_STORAGE`
     * @returns `SharedPreferences`: A instance of `SharedPreferences`.
     */
    public static getSharedPreferences(
        name: string,
        mode: SharedPreferenceMode = SharedPreferenceMode.SESSION_STORAGE,
    ): SharedPreferences {
        return new SharedPreferences(name, mode);
    }

    /**
     * Clears the specified shared preferences.
     *
     * This method iterates over the provided array of preference names,
     * creates a `SharedPreferences` editor for each preference,
     * clears the preference, and commits the changes.
     *
     * @param preferences - An array of strings representing the
     * names of the preferences to be cleared.
     */
    public static clearSharedPreferences(preferences: string[]) {
        preferences.forEach((preference) => {
            const sharedPreferenceEditor = new SharedPreferences(preference).edit();
            sharedPreferenceEditor.clear();
            sharedPreferenceEditor.commit();
        });
    }
}
